import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, CalendarCheck, GraduationCap, Shield, Sprout, Star } from 'lucide-react';
import SEO from '../components/SEO';
import BookButton from '../components/BookButton';
import Marquee from '../components/Marquee';
import { EASE, MaskedLines, MaskedLinesOnView, ParallaxImage, Reveal } from '../components/Reveal';
import { BLOG_POSTS } from '../data/blog';
import { EXPECT_STEPS, IMG, SERVICES } from '../data/site';

const MARQUEE_ITEMS = [
  'Therapeutic Massage',
  'Prenatal & Postpartum',
  'Deep Tissue',
  'Lymphatic Drainage',
  'Veteran Care',
  'Humble, Texas',
];

function Hero() {
  return (
    <section className="relative min-h-screen overflow-hidden bg-brand-cream" data-testid="home-hero">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-1/3 h-[560px] w-[560px] rounded-full bg-brand-mist/40 blur-3xl"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-20 pt-32 md:px-10 lg:grid-cols-12 lg:gap-8 lg:pt-36">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-teal"
          >
            <span className="h-px w-10 bg-brand-teal/40" aria-hidden="true" />
            Thula Doula Wellness — Humble, Texas
          </motion.p>

          <MaskedLines
            as="h1"
            delay={0.25}
            lines={[
              <span key="a">Healing through</span>,
              <span key="b" className="italic text-brand-teal">
                skilled hands.
              </span>,
            ]}
            className="mt-8 font-display text-[clamp(3.4rem,9.5vw,7.5rem)] font-light leading-[0.98] tracking-tight text-brand-ink"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
            className="mt-8 max-w-lg text-lg leading-relaxed text-brand-moss"
          >
            Therapeutic, prenatal, and medical massage in a calm, clinically-grounded space — a
            place where every body can finally exhale.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <BookButton size="lg" testId="hero-book-btn" />
            <Link
              to="/massage-services"
              data-testid="hero-explore-link"
              className="group inline-flex items-center gap-2 rounded-full border border-brand-ink/25 px-8 py-4 text-base font-semibold text-brand-ink transition-[background-color,color,border-color] duration-300 hover:border-brand-ink hover:bg-brand-ink hover:text-brand-cream"
            >
              Explore massage services
              <ArrowRight className="size-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-[13px] font-medium text-brand-moss"
          >
            {['Licensed & insured', 'Prenatal & postpartum trained', 'VA Community Care welcome', 'HSA & FSA accepted'].map(
              (chip) => (
                <li key={chip} className="flex items-center gap-2">
                  <span className="size-1.5 rounded-full bg-brand-teal" aria-hidden="true" />
                  {chip}
                </li>
              )
            )}
          </motion.ul>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: 'inset(100% 0 0 0)' }}
            animate={{ clipPath: 'inset(0% 0 0 0)' }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
            className="relative mx-auto max-w-md overflow-hidden rounded-b-[2rem] rounded-t-[999px]"
          >
            <ParallaxImage
              src={IMG.hero}
              alt="Therapeutic back massage in a calm treatment room"
              className="aspect-[3/4] w-full"
              strength={28}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.3 }}
            className="float-soft absolute -left-4 bottom-10 rounded-2xl bg-brand-cream/90 p-5 shadow-[0_24px_60px_-24px_rgba(19,42,38,0.45)] backdrop-blur-md md:-left-10"
          >
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-teal">
              <CalendarCheck className="size-4" /> Now booking
            </p>
            <p className="mt-2 text-sm font-medium text-brand-ink">
              Instant online scheduling
              <br />
              powered by Jane
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Philosophy() {
  return (
    <section className="bg-brand-cream py-24 md:py-36" data-testid="home-philosophy">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-teal">
              <span className="h-px w-10 bg-brand-teal/40" aria-hidden="true" /> Our philosophy
            </p>
          </Reveal>
          <MaskedLinesOnView
            as="h2"
            lines={['Massage is not an', 'indulgence — it is', <span key="c" className="italic">healthcare.</span>]}
            className="mt-8 font-display text-[clamp(2.4rem,5vw,4.5rem)] font-light leading-[1.05] tracking-tight text-brand-ink"
          />
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-brand-moss md:text-lg">
              Every session at Thula begins with an unhurried intake and ends with a plan. We blend
              clinical, medically-informed technique with the warmth of doula care — because skilled
              hands matter most when they pay attention.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <Link
              to="/about"
              data-testid="philosophy-about-link"
              className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-brand-teal"
            >
              <span className="link-underline">More about us</span>
              <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="flex items-end lg:col-span-5">
          <Reveal delay={0.15} className="w-full">
            <ParallaxImage
              src={IMG.studio}
              alt="A quiet treatment room at Thula Doula Wellness"
              className="aspect-[4/3] w-full rounded-3xl"
              strength={24}
            />
            <p className="mt-4 text-xs uppercase tracking-[0.25em] text-brand-moss/70">
              Our treatment room — Wilson Rd, Humble
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ServicesIndex() {
  return (
    <section className="bg-brand-parchment py-24 md:py-32" data-testid="home-services">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <MaskedLinesOnView
            as="h2"
            lines={['What we practice.']}
            className="font-display text-[clamp(2.4rem,5vw,4.25rem)] font-light leading-none tracking-tight text-brand-ink"
          />
          <Reveal>
            <Link
              to="/massage-services"
              data-testid="services-index-all-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-brand-teal"
            >
              <span className="link-underline">All services</span>
              <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 border-t border-brand-ink/10">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.05}>
              <Link
                to={`/massage-services#${s.slug}`}
                data-testid={`home-service-row-${s.slug}`}
                className="group relative grid grid-cols-1 items-center gap-6 overflow-hidden border-b border-brand-ink/10 py-8 transition-colors duration-500 hover:bg-brand-cream md:grid-cols-12 md:py-10"
              >
                <span className="font-display text-lg text-brand-teal/60 md:col-span-1 md:pl-2">
                  {s.num}
                </span>
                <h3 className="font-display text-3xl font-light tracking-tight text-brand-ink transition-transform duration-500 group-hover:translate-x-2 md:col-span-5 md:text-4xl">
                  {s.name}
                  {s.comingSoon && (
                    <span
                      data-testid="body-sculpting-pill"
                      className="ml-4 inline-block -translate-y-1 rounded-full bg-brand-mist px-3 py-1 align-middle text-[10px] font-sans font-bold uppercase tracking-[0.18em] text-brand-teal"
                    >
                      Coming soon
                    </span>
                  )}
                </h3>
                <p className="max-w-md text-sm leading-relaxed text-brand-moss md:col-span-4">
                  {s.short}
                </p>
                <span className="hidden justify-end md:col-span-2 md:flex md:pr-2">
                  <span className="inline-flex size-12 items-center justify-center rounded-full border border-brand-ink/15 transition-[background-color,color,transform] duration-500 group-hover:rotate-45 group-hover:border-brand-teal group-hover:bg-brand-teal group-hover:text-brand-cream">
                    <ArrowUpRight className="size-5" />
                  </span>
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="mt-6 text-sm text-brand-moss/80">
            Not sure where to start? Book a therapeutic session and we’ll point you in the
            right direction — or call{' '}
            <a href="tel:+18323508516" data-testid="services-phone-link" className="underline decoration-brand-sage underline-offset-4 hover:text-brand-teal">
              (832) 350-8516
            </a>
            .
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function QuoteBand() {
  return (
    <section className="relative" data-testid="home-quote-band">
      <div className="relative h-[70vh] min-h-[480px] overflow-hidden">
        <ParallaxImage
          src={IMG.calm}
          alt="A quiet moment of rest"
          className="absolute inset-0 h-full"
          strength={60}
          imgClassName="h-full"
        />
        <div className="absolute inset-0 bg-brand-ink/55" aria-hidden="true" />
        <div className="relative flex h-full items-center">
          <div className="mx-auto w-full max-w-7xl px-5 md:px-10">
            <MaskedLinesOnView
              as="blockquote"
              lines={['Skilled hands. Attentive care.', 'A space where every body', 'can finally exhale.']}
              className="max-w-4xl font-display text-[clamp(2.2rem,5.5vw,4.75rem)] font-light italic leading-[1.08] tracking-tight text-brand-cream"
            />
            <Reveal delay={0.4}>
              <p className="mt-8 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-brand-cream/70">
                <Star className="size-4 text-brand-sage" aria-hidden="true" /> The Thula promise
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function TwoPaths() {
  return (
    <section className="bg-brand-cream py-24 md:py-32" data-testid="home-two-paths">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <MaskedLinesOnView
          as="h2"
          lines={['Beyond the massage table.']}
          className="max-w-2xl font-display text-[clamp(2.4rem,5vw,4.25rem)] font-light leading-[1.05] tracking-tight text-brand-ink"
        />
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Link
              to="/veterans"
              data-testid="home-veterans-card"
              className="group relative flex h-full min-h-[380px] flex-col justify-between overflow-hidden rounded-3xl bg-brand-ink p-8 text-brand-cream transition-transform duration-500 md:p-10"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-brand-teal/40 blur-3xl transition-opacity duration-500 group-hover:opacity-80"
              />
              <div className="relative">
                <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-sage">
                  <Shield className="size-4" aria-hidden="true" /> For veterans
                </p>
                <h3 className="mt-6 font-display text-3xl font-light tracking-tight md:text-4xl">
                  Massage therapy for those who served
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-brand-cream/70 md:text-base">
                  Therapeutic and medical massage tailored to veterans — including VA Community Care
                  referrals and authorization billing.
                </p>
              </div>
              <span className="relative mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-brand-sage">
                <span className="link-underline">Veteran services</span>
                <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-4">
            <Link
              to="/virtual-doula"
              data-testid="home-doula-card"
              className="group relative flex h-full min-h-[380px] flex-col justify-between overflow-hidden rounded-3xl bg-brand-sage/50 p-8 transition-transform duration-500 md:p-10"
            >
              <div>
                <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-teal">
                  <Sprout className="size-4" aria-hidden="true" /> Virtual doula support
                </p>
                <h3 className="mt-6 font-display text-3xl font-light tracking-tight text-brand-ink md:text-4xl">
                  Steady guidance, from anywhere
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-brand-moss md:text-base">
                  Birth planning, comfort measures, and postpartum preparation over secure video —
                  virtual-only at this time.
                </p>
                <p
                  data-testid="home-doula-booked-note"
                  className="mt-5 inline-block rounded-full bg-brand-cream px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-brand-teal"
                >
                  Booked through May 2027
                </p>
              </div>
              <span className="mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-brand-teal">
                <span className="link-underline">About virtual doula care</span>
                <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </Reveal>
          <Reveal delay={0.24} className="lg:col-span-4">
            <a
              href="https://www.skool.com/massage-practice-hub-3277/about"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-hub-card"
              className="group relative flex h-full min-h-[380px] flex-col justify-between overflow-hidden rounded-3xl bg-brand-mist/60 p-8 transition-transform duration-500 md:p-10"
            >
              <div>
                <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-teal">
                  <GraduationCap className="size-4" aria-hidden="true" /> For practitioners
                </p>
                <h3 className="mt-6 font-display text-3xl font-light tracking-tight text-brand-ink md:text-4xl">
                  Massage Practice Hub
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-brand-moss md:text-base">
                  Coaching for massage therapists and wellness practitioners — business foundations,
                  compliance, and client care. Powered by Thula Wellness.
                </p>
                <p className="mt-5 inline-block rounded-full bg-brand-cream px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-brand-teal">
                  Coaching program
                </p>
              </div>
              <span className="mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-brand-teal">
                <span className="link-underline">Join the Hub</span>
                <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WhatToExpect() {
  return (
    <section className="bg-brand-cream pb-24 md:pb-32" data-testid="home-expect">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="border-t border-brand-ink/10 pt-16 md:pt-24">
          <MaskedLinesOnView
            as="h2"
            lines={['Simple to start.', 'Easy to return to.']}
            className="font-display text-[clamp(2.2rem,4.5vw,3.75rem)] font-light leading-[1.05] tracking-tight text-brand-ink"
          />
          <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3">
            {EXPECT_STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.12}>
                <div className="border-t-2 border-brand-sage pt-6">
                  <span className="font-display text-5xl font-light italic text-brand-teal/70">
                    {step.num}
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-brand-ink">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-moss">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BlogPreview() {
  const posts = BLOG_POSTS.slice(0, 3);
  return (
    <section className="bg-brand-parchment py-24 md:py-32" data-testid="home-blog-preview">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <MaskedLinesOnView
            as="h2"
            lines={['From the journal.']}
            className="font-display text-[clamp(2.2rem,4.5vw,3.75rem)] font-light leading-none tracking-tight text-brand-ink"
          />
          <Reveal>
            <Link
              to="/blog"
              data-testid="home-blog-all-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-brand-teal"
            >
              <span className="link-underline">Read the blog</span>
              <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3">
          {posts.map((post, i) => (
            <Reveal key={post.slug} delay={i * 0.1}>
              <Link
                to={`/blog/${post.slug}`}
                data-testid={`home-blog-card-${post.slug}`}
                className="group block"
              >
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={post.cover}
                    alt={post.title}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  />
                </div>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-teal">
                  {post.category} · {post.readTime}
                </p>
                <h3 className="mt-2 font-display text-2xl font-normal leading-snug tracking-tight text-brand-ink transition-colors duration-300 group-hover:text-brand-teal">
                  {post.title}
                </h3>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeCta() {
  return (
    <section className="bg-brand-cream py-24 md:py-32" data-testid="home-cta">
      <div className="mx-auto max-w-7xl px-5 text-center md:px-10">
        <MaskedLinesOnView
          as="h2"
          lines={['Ready to exhale?']}
          className="font-display text-[clamp(3rem,8vw,6.5rem)] font-light italic leading-none tracking-tight text-brand-ink"
        />
        <Reveal delay={0.2}>
          <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-brand-moss">
            Weekday appointments fill quickly — reserve your table in a couple of
            minutes.
          </p>
        </Reveal>
        <Reveal delay={0.35}>
          <div className="mt-10 flex justify-center">
            <BookButton size="lg" testId="home-cta-book-btn" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="Thula Doula Wellness | Therapeutic & Prenatal Massage — Humble, TX"
        description="Therapeutic, prenatal & postpartum, deep tissue, and lymphatic drainage massage in Humble, Texas. VA Community Care welcome. Book a massage online 24/7."
      />
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <Philosophy />
      <ServicesIndex />
      <QuoteBand />
      <TwoPaths />
      <WhatToExpect />
      <BlogPreview />
      <HomeCta />
    </>
  );
}
